import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardControlKeyIcon from '@mui/icons-material/KeyboardControlKey';
import React from 'react';
import { DemeterPageElementModel, GetDemeterChartSeriesDataResponse } from '../../../../Generated/Raven-Demeter';
import { chartColors } from '../../../Components/Charts/ChartDefinitions';
import useSearchParameters from '../../../Components/Navigation/Hooks/useSearchParametersHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from '../ChartBuilder.module.scss';
import { ChartBuilderRequest, DataSourceSelection, defaultDataSource } from '../ChartBuilderDefinitions';
import ChartBuilderDataSourceOptions from './ChartBuilderDataSourceOptions';
import ChartBuilderMathOperationOptions from './ChartBuilderMathOperationsOptions';

interface IChartBuilderDataSeriesOptionsProps {
    chartBuilderUpsertRequest: ChartBuilderRequest;
    setChartBuilderUpsertRequest: (chartBuilderRequest: ChartBuilderRequest) => void;
    pageElements: DemeterPageElementModel[];
    listDemeterChartDataResponse?: GetDemeterChartSeriesDataResponse[];
}

const maximumNumberOfDataSources = 2;

const ChartBuilderDataSeriesOptions: React.FC<IChartBuilderDataSeriesOptionsProps> = (props: IChartBuilderDataSeriesOptionsProps) => {
    // Application hooks.
    const [translations] = useLanguage();
    const [searchParameters] = useSearchParameters();
    const dataSeriesIndex = +(searchParameters.dataSeriesTab?.replace('dataSeries', '') || 0);

    // Helplers.
    const currentDataSeries = [...props.chartBuilderUpsertRequest.rows];
    const currentDataSources = props.chartBuilderUpsertRequest.rows[dataSeriesIndex]?.sources!;
    const allDataSourceColorsInUse = props.chartBuilderUpsertRequest.rows.map((y) => y.sources.map((z) => (z as DataSourceSelection).color)).flat();
    const allDataSeriesColorsInUse = props.chartBuilderUpsertRequest.rows.map((y) => y.color);
    const allColorsInUse = [...allDataSeriesColorsInUse, ...allDataSourceColorsInUse] as string[];

    const handleAddDataSourceSection = () => {
        const sourceColor = chartColors.lineChartColorsRuleSetOne.find((x) => !allColorsInUse.includes(x));
        const rowColor = chartColors.lineChartColorsRuleSetOne.find((x) => !allColorsInUse.includes(x) && x !== sourceColor);
        props.setChartBuilderUpsertRequest({
            ...props.chartBuilderUpsertRequest,
            rows: currentDataSeries?.map((x, index) => {
                if (index === dataSeriesIndex) {
                    return {
                        ...x,
                        color: rowColor,
                        sources: [...currentDataSources!, { ...defaultDataSource, color: sourceColor, filterTimeSpan: currentDataSeries[0].filterTimeSpan }],
                    };
                }

                return x;
            }),
        });
    };

    const removeDataSource = (currentDataSourceIndex: number) => {
        props.setChartBuilderUpsertRequest({
            ...props.chartBuilderUpsertRequest,
            rows: currentDataSeries.map((x, index) => {
                if (index === dataSeriesIndex) {
                    return {
                        ...x,
                        dataCombinationType: undefined,
                        sources: x.sources.filter((y, dataSourceIndex) => dataSourceIndex !== currentDataSourceIndex),
                    };
                }

                return x;
            }),
        });
    };

    return (
        <div className={styles.chart_builder_data_series_section}>
            {currentDataSources?.map((_, dataSourceIndex) => (
                <div className={styles.chart_builder_data_source_options}>
                    {dataSourceIndex > 0 && (
                        <ChartBuilderMathOperationOptions
                            chartBuilderUpsertRequest={props.chartBuilderUpsertRequest}
                            setChartBuilderUpsertRequest={props.setChartBuilderUpsertRequest}
                        />
                    )}

                    <ChartBuilderDataSourceOptions
                        chartBuilderUpsertRequest={props.chartBuilderUpsertRequest}
                        setChartBuilderRequest={props.setChartBuilderUpsertRequest}
                        pageElements={props.pageElements}
                        listDemeterChartDataResponse={props.listDemeterChartDataResponse}
                        dataSourceIndex={dataSourceIndex}
                        colorsInUse={allColorsInUse}
                    />

                    {dataSourceIndex === currentDataSeries.length - 1 && (
                        <ChartBuilderDataSourceOptions
                            chartBuilderUpsertRequest={props.chartBuilderUpsertRequest}
                            setChartBuilderRequest={props.setChartBuilderUpsertRequest}
                            pageElements={props.pageElements}
                            listDemeterChartDataResponse={props.listDemeterChartDataResponse}
                            isCalculatedSeries
                            colorsInUse={allColorsInUse}
                        />
                    )}
                    {currentDataSources!.length < maximumNumberOfDataSources && (
                        <div className={styles.chart_builder_line_break}>
                            <div className={styles.chart_builder_remove_calculated_series_button}>
                                <button type="button" onClick={handleAddDataSourceSection}>
                                    {translations.chartBuilder.text.createCalculatedSeries}
                                    <KeyboardArrowDownIcon />
                                </button>
                            </div>
                        </div>
                    )}
                    {dataSourceIndex > 0 && (
                        <div className={styles.chart_builder_line_break}>
                            <div className={styles.chart_builder_remove_calculated_series_button}>
                                <button type="button" onClick={() => removeDataSource(currentDataSources.length - 1)}>
                                    {translations.chartBuilder.text.removeCalculatedSeries}
                                    <KeyboardControlKeyIcon />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default ChartBuilderDataSeriesOptions;
